import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { IVasInfo } from '@r1-frontend/api-domru/api-profile/v1/vas/dto/ResponseDto';

import { btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { Label } from '@r1-frontend/ui-react/components/label';
import { NonStaticContainer } from '@r1-frontend/ui-react/experimental/containers';
import { RubleSymbol } from '@r1-frontend/ui-react/experimental/purchase/cost/Cost/RubleSymbol';
import { AdvertisingLabelWithTooltip } from '@r1-frontend/ui-react/organisms/AdvertisingLabelWithTooltip';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { useWebp } from '@r1-frontend/shared/hooks/useWebp';

import { MultibundlePrice } from '~/src/components/MultibundlePrice';
import { USER_LOGIN_ROUTE } from '~/src/constants/routes';
import { IPriceOptions } from '~/src/features/domru-tv/packages/types';
import { TState } from '~/src/store';
import { IMultibundleAdditional } from '~/src/store/selectors/vas';

import * as ST from './styled';

export const multibundleLink = '/lk/multibundle';

interface IProps {
    multibundle: IVasInfo<IMultibundleAdditional>,
    onBtnClick: () => void,
    className?: string,
}

const MultibundleCard = ({ multibundle, onBtnClick, className }: IProps): JSX.Element => {
    const { additionalProps, contentData, image, imageWebp, pricesV2, advertising } = multibundle;
    const { priceOptions } = additionalProps || {};

    const promoInfo = useMemo(() => pricesV2?.find(price => !!price.lifeTimePromoTypeId), [pricesV2]);

    const isAuth = useSelector((state: TState) => state.auth.isAuth);

    const router = useRouter();

    const widgetBgImage = useWebp() ? imageWebp : image;

    const onClickHandler = useCallback(() => {
        onBtnClick();

        router.push(isAuth ? multibundleLink : `${USER_LOGIN_ROUTE}?referrer=${multibundleLink}`);
    }, [isAuth, onBtnClick, router]);

    return (
        <ST.WidgetWrapper className={className} bgImage={widgetBgImage}>
            <ST.WidgetContent withPromo={!!promoInfo}>
                {promoInfo && (
                    <Label
                        textColor={COLORS.LabelsText_2}
                        bgColor={COLORS.LabelsBg_2}
                    >
                        {`${promoInfo.duration} месяц за ${promoInfo.value}`}&nbsp;{RubleSymbol}
                    </Label>
                )}
                <ST.WidgetName>Ещё больше{'\n'}онлайн-кинотеатров, фильмов и сериалов</ST.WidgetName>
                {priceOptions && (
                    <ST.CountPricesBlock>
                        {(priceOptions as unknown as IPriceOptions[]).map((priceOption, index) => {
                            if (index > 1) {
                                return null;
                            }

                            const priceColor = index === 1 ? COLORS.LabelsText_2 : COLORS.Digital_Blue;

                            return (<MultibundlePrice
                                key={priceOption.count}
                                priceColor={priceColor}
                                discount={priceOption.discount}
                                count={index === 0 ? '1 ' : ''}
                                price={priceOption.price}
                            />
                            );
                        })}
                    </ST.CountPricesBlock>
                )}
                {contentData?.body && (
                    <ST.WidgetInfo dangerouslySetInnerHTML={{ __html: contentData.body }}/>
                )}
                <ST.WidgetButton
                    type={btnTypes.ACCENT}
                    size={btnSizes.SMALL}
                    text={'Выбрать подписки'}
                    onClick={onClickHandler}
                />
                {advertising &&
                    <NonStaticContainer
                        $united={{ $top: 24, $right: 24 }}
                    >
                        <AdvertisingLabelWithTooltip
                            companyName={advertising.companyName}
                            erid={advertising.erid}
                            tooltip={{
                                position: 'top-right',
                            }}
                        />
                    </NonStaticContainer>
                }
            </ST.WidgetContent>
        </ST.WidgetWrapper>
    );
};

export default MultibundleCard;
